import { Stack } from '@fluentui/react';
import { useCallback, memo } from 'react';
import { useMutation } from '@tanstack/react-query';
import shallow from 'zustand/shallow';
import { ApiCalls } from '../../../api/api';
import { DateResolution, EntityType, GridCell, GridColumnRow, GridRow } from '../../../api/generated/data-contracts';
import { useStore } from '../../../context/store';
import { Toolbar } from './components/Toolbar/Toolbar';
import { AllocationGrid } from './components/Grid/components/AllocationGrid';
import { RecoilRoot } from 'recoil';
import { AddRowInfo, OnUpdateCellInfo } from '../_grid/useGrid';
import { AllocationView } from '../projectCapacity/regular/Allocation';
import { findMap } from '../_grid/helpers/map';
import { AddRowData } from './components/Grid/components/AddRowData';
import { ITimeline } from '../../../context/contexts/timelineContext';
import * as DateEx from '../../../Utilities/DateEx';
import { useUISettings } from '../../../context/network/http/QueryProvider/queries/UISettings';
import { TPError2 } from '../../../Entities/TPError';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { stringVariantsCamelCase } from '../../../helpers/stringVariants';

export const Allocation = () => {
    const [useLegacyAllocation, setUseLegacyAllocation] = useLocalStorage('tp-allocation-view-use-legacy', true);
    if (useLegacyAllocation) {
        return <AllocationView setUseLegacyAllocation={setUseLegacyAllocation} />;
    }
    return (
        <RecoilRoot>
            <AllocationInner setUseLegacyAllocation={setUseLegacyAllocation} />
        </RecoilRoot>
    );
};

export const AllocationInner = memo(function AllocationInner({
    setUseLegacyAllocation,
}: {
    setUseLegacyAllocation: React.Dispatch<React.SetStateAction<boolean>>;
}) {
    const { timelineResolution, addWarningNotification } = useStore(store => {
        return {
            timelineResolution: store.timeline.resolution,
            addWarningNotification: store.addWarningNotification,
        };
    }, shallow);

    const { mutateAsync } = useMutation({
        mutationFn: (updates: OnUpdateCellInfo[]) =>
            ApiCalls.addActivities(
                updates.map(({ newValue, relations, row, columnIndex }) => {
                    const [contract] = relations;
                    const contractId = contract.identity.id;
                    const resourceId = row.identity.id;
                    const start = row.columnRows[columnIndex].start;
                    const end = row.columnRows[columnIndex].end;
                    return {
                        contractId,
                        resourceId,
                        work: newValue.toNumber(),
                        type: 2,
                        resolution: timelineResolution as DateResolution,
                        start,
                        end,
                    };
                }),
            ).then(res => res.data),
    });

    const onUpdateCell = useCallback(
        async (updates: OnUpdateCellInfo[]) => {
            await mutateAsync(updates);
        },
        [mutateAsync],
    );

    const { data: uiSettings } = useUISettings();

    const { mutateAsync: enrichNewRow } = useMutation({
        mutationFn: async ({ row, updateRows }: { row: GridRow; updateRows: (row: GridRow) => any }) =>
            ApiCalls.enrichAllocationGridRow(row)
                .then(res => res.data)
                .then(updateRows),
        onError: (error: any) => {
            console.error('ProjectPlannerInner ~ enrichNewRow:', error);
            if (error instanceof TPError2) {
                // Network error
                addWarningNotification(error.Message);
            } else if (error instanceof Error) {
                addWarningNotification(error.message);
            }
        },
    });

    const onAddRow = useCallback(
        ({ parentRow, rows, abort, updateRows }: AddRowInfo, { resource, timelineInfo, droppedOnTopRow }: AddRowData) => {
            const existingRow = findMap(rows, row => {
                // check for worktype 2 (allocation) as well, to allow allocation on named requests
                return row.identity.id === resource.id && row.parentRowId === parentRow?.id && row.metadata['WorkType'] === 2;
            });

            if (existingRow) {
                addWarningNotification(`${resource.name} is already added to the ${existingRow.name} contract`);
                return abort();
            }

            const getResourceRow = (parentRowId?: string) => {
                const newRow: GridRow = {
                    id: crypto.randomUUID(),
                    parentRowId,
                    name: resource.name,
                    identity: { id: resource.id, entityType: EntityType.Resource },
                    columnRows: makeColumnRows(timelineInfo, uiSettings.settings.useISO8601),
                    metadata: {
                        Active: true,
                        ResourceType: 1,
                        WorkType: 2,
                    },
                    totalColumn: {
                        cells: [makeCell(), makeCell()],
                        id: crypto.randomUUID(),
                        metadata: null,
                    },
                };
                enrichNewRow({
                    row: newRow,
                    // We pass on update rows to let useMutation handle possible errors
                    updateRows,
                });
                return newRow;
            };

            if (droppedOnTopRow) {
                const contractRowId = crypto.randomUUID();
                // TODO: support dynamic contract names with: const field = uiSettings.settings.enableDynamicContractNames ? language.CapacityViews.NameLabel : uiSettings.settings.contractDefaultName;
                const field = uiSettings.settings.contractDefaultName;
                return [
                    // Contract row
                    {
                        id: contractRowId,
                        parentRowId: null,
                        name: stringVariantsCamelCase.getAsStringValue(resource, field) || resource.name,
                        identity: { entityType: EntityType.Contract, id: contractRowId },
                        columnRows: makeColumnRows(timelineInfo, uiSettings.settings.useISO8601),
                        metadata: null,
                        totalColumn: {
                            cells: [makeCell(), makeCell()],
                            id: crypto.randomUUID(),
                            metadata: null,
                        },
                    },
                    // Resource row
                    getResourceRow(contractRowId),
                ] as GridRow[];
            }
            return getResourceRow(parentRow?.id);
        },
        [addWarningNotification, enrichNewRow, uiSettings.settings.contractDefaultName, uiSettings.settings.useISO8601],
    );

    return (
        <Stack styles={{ root: { width: '100%', height: '100%' } }}>
            <Toolbar setUseLegacyAllocation={setUseLegacyAllocation} />
            <AllocationGrid
                //
                onUpdateCells={onUpdateCell}
                onAddRow={onAddRow}
                defaultExpandedState={true}
            />
        </Stack>
    );
});

const makeCell = (gridCell?: GridCell): GridCell => {
    return {
        ...gridCell,
        disabled: false,
        id: crypto.randomUUID(),
        value: 0,
    };
};

const getAdder = (resolution: DateResolution) => {
    switch (resolution) {
        case DateResolution.Day:
            return DateEx.addDay;
        case DateResolution.Month:
            return DateEx.addMonth;
        case DateResolution.Quarter:
            return DateEx.addQuarter;
        case DateResolution.Week:
            return DateEx.addWeek;
        case DateResolution.Year:
            return DateEx.addYear;
        default: {
            throw new Error(`Unknown timeline resolution: ${resolution}}`);
        }
    }
};

const getLastDayOfPeriodFn = (resolution: DateResolution) => {
    switch (resolution) {
        case DateResolution.Day:
            // noop
            return (date: Date) => date;
        case DateResolution.Month:
            return DateEx.lastDayOfMonth;
        case DateResolution.Quarter:
            return DateEx.lastDayOfQuarter;
        case DateResolution.Week:
            return DateEx.lastDayOfWeek;
        case DateResolution.Year:
            return DateEx.lastDayOfYear;
        default: {
            throw new Error(`Unknown timeline resolution: ${resolution}}`);
        }
    }
};

function makeColumnRows({ start, end, resolution }: ITimeline, useISO8601: boolean): GridColumnRow[] {
    const periods: GridColumnRow[] = [];
    let currentStartDate = start;

    const adder = getAdder(resolution);
    const getLastDayOfPeriod = getLastDayOfPeriodFn(resolution);

    while (currentStartDate <= end) {
        let currentEndDate: Date = getLastDayOfPeriod(currentStartDate, useISO8601);

        // Make sure the period does not exceed the overall endDate
        if (currentEndDate > end) {
            currentEndDate = end;
        }

        periods.push({
            cells: [null, makeCell()],
            id: crypto.randomUUID(),
            start: new Date(currentStartDate),
            end: new Date(currentEndDate),
        });

        // Prepare the next period's start date
        currentStartDate = adder(currentStartDate);
    }

    return periods;
}
